/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import type { ShopperProducts } from '@/scapi';
import {
    assignStJudeGlobal,
    buildCategoryBreadcrumbSegment,
    buildStJudePagePath,
    wrapStJudeGlobal,
} from '@/lib/tracking/stjude-datalayer';
import { useStJudeRootLoaderData } from './use-stjude-root-loader-data';

export interface StJudeCategoryDataLayerSyncProps {
    category: ShopperProducts.schemas['Category'];
}

/**
 * Corrects `window.stjude.digitalData.page` with the category's full
 * `/s/{siteId}/{categoryBreadcrumb}` path once this route's own loader data
 * is available — the root loader (and `StJudeDataLayerSync`, which
 * explicitly skips this route — see its doc comment) only knows the bare
 * category ID from the URL, not the category tree.
 *
 * Mount inside the category page route component (alongside
 * `CategoryBreadcrumbs`, which reads the same `category.parentCategoryTree`).
 * Non-visual (renders nothing).
 */
export function StJudeCategoryDataLayerSync({ category }: StJudeCategoryDataLayerSyncProps) {
    const rootData = useStJudeRootLoaderData();

    useEffect(() => {
        if (!rootData?.omnitureConfig?.omnitureEnabled || !rootData.stJudeDigitalData || !rootData.stJudeSiteId) {
            return;
        }

        const breadcrumb = buildCategoryBreadcrumbSegment(category);

        assignStJudeGlobal(
            wrapStJudeGlobal({
                ...rootData.stJudeDigitalData,
                page: {
                    ...rootData.stJudeDigitalData.page,
                    pageName: buildStJudePagePath(rootData.stJudeSiteId, breadcrumb),
                    type: 'category',
                },
            })
        );
    }, [category, rootData]);

    return null;
}
